import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/** How long "Added to cart" stays on the button. */
const ADDED_STATE_DURATION = 2500;

/** Gives up waiting for the cart response after this long and restores the button. */
const ADD_TIMEOUT = 8000;

/** The bar covers about this much of the bottom of the screen; content behind it counts as hidden. */
const BAR_OVERLAP = 80;

const MOBILE_QUERY = '(max-width: 749px)';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {HTMLElement & {
 *   hasMissingOption: boolean,
 *   validateOptions: () => boolean
 * }} ProductForm
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 *
 * The bar shows while neither the size picker nor the main add to cart button is on screen:
 * on phones also before the shopper reaches them (when enabled), elsewhere only once they
 * have been scrolled past. It stays over the footer too; the page reserves its height at the
 * bottom so the last footer row is not covered. Without a chosen size the button takes the
 * shopper to the sizes instead of adding a default one.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #targetsObserver = null;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  /** @type {Map<Element, boolean>} */
  #visibleTargets = new Map();

  /** @type {Element | null} */
  #watchTarget = null;

  /** @type {Element | null} */
  #pickerTarget = null;

  #isStuck = false;

  #isAdding = false;

  /** @type {number | undefined} */
  #addTimeout;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller per connection: a reconnect (section re-render) reused the aborted one
    this.#abortController = new AbortController();
    this.#visibleTargets.clear();
    this.#setupIntersectionObservers();
    this.#reserveBarSpace();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#mobileQuery.addEventListener('change', this.#updateVisibility, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#targetsObserver?.disconnect();
    this.#barResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--satc-space');
    this.#abortController.abort();
    clearTimeout(this.#addTimeout);
    clearTimeout(this.#resetTimeout);
  }

  /**
   * Watches the size picker and the main add to cart button.
   */
  #setupIntersectionObservers() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Watch the main add-to-cart button itself: the block also holds content below it
    // (social proof, shipping badge) that would keep the bar hidden after the button is gone.
    this.#watchTarget = productForm.querySelector('[ref="addToCartButton"]') ?? buyButtonsBlock;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#pickerTarget = this.#getVariantPicker();

    this.#targetsObserver = new IntersectionObserver(this.#handleTargetsIntersection, {
      rootMargin: `0px 0px -${BAR_OVERLAP}px 0px`,
    });
    this.#targetsObserver.observe(this.#watchTarget);
    if (this.#pickerTarget) this.#targetsObserver.observe(this.#pickerTarget);
  }

  /**
   * Publishes the bar's height as --satc-space so the page can leave room under the footer.
   */
  #reserveBarSpace() {
    const { stickyBar } = this.refs;
    this.#barResizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--satc-space', `${Math.ceil(stickyBar.offsetHeight)}px`);
    });
    this.#barResizeObserver.observe(stickyBar);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleTargetsIntersection = (entries) => {
    for (const entry of entries) {
      this.#visibleTargets.set(entry.target, entry.isIntersecting);
    }
    this.#updateVisibility();
  };

  #updateVisibility = () => {
    if (!this.#watchTarget) return;

    const targetOnScreen = [this.#watchTarget, this.#pickerTarget].some(
      (target) => target && this.#visibleTargets.get(target)
    );

    let show = !targetOnScreen;

    if (show && !this.#showsBeforeTargets) {
      // Only once the main button has been scrolled past, not before reaching it.
      show = this.#watchTarget.getBoundingClientRect().bottom < 0;
    }

    if (show) this.#showStickyBar();
    else this.#hideStickyBar();
  };

  get #showsBeforeTargets() {
    return this.dataset.showBeforeButtons === 'true' && this.#mobileQuery.matches;
  }

  // Public action handlers
  /**
   * Adds to cart through the main product form, or points to the sizes when none is chosen.
   */
  handleAddToCartClick = () => {
    const productForm = this.#getProductForm();
    const button = this.#targetAddToCartButton;
    if (!productForm || !button) return;

    if (productForm.hasMissingOption) {
      productForm.validateOptions();
      return;
    }

    if (button.disabled || this.#isAdding) return;

    this.#isAdding = true;
    this.#setButtonState('loading');
    clearTimeout(this.#resetTimeout);
    clearTimeout(this.#addTimeout);
    this.#addTimeout = window.setTimeout(() => this.#finishAdd(false), ADD_TIMEOUT);

    button.dataset.puppet = 'true';
    button.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

      // Keep watching the live button if the variant render replaced it
      const button = this.#targetAddToCartButton;
      if (button && button !== this.#watchTarget && this.#targetsObserver) {
        if (this.#watchTarget) {
          this.#targetsObserver.unobserve(this.#watchTarget);
          this.#visibleTargets.delete(this.#watchTarget);
        }
        this.#targetsObserver.observe(button);
        this.#watchTarget = button;
      }
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // The morph restores the server state; keep an add in progress visible.
    if (this.#isAdding) this.#setButtonState('loading');

    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.satc__variant');
    const variantPicker = this.#getVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Resets the puppet flag and settles the button once the cart answers.
   * @param {CustomEvent} event - The cart update or cart error event
   */
  #handleCartAddComplete = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#isAdding) return;

    if (event.type === ThemeEvents.cartError) {
      this.#finishAdd(false);
      return;
    }

    const data = event.detail?.data ?? {};
    if (data.source !== 'product-form-component' || String(data.productId) !== String(this.dataset.productId)) return;

    this.#finishAdd(!data.didError);
  };

  /**
   * @param {boolean} success
   */
  #finishAdd(success) {
    if (!this.#isAdding) return;

    this.#isAdding = false;
    clearTimeout(this.#addTimeout);

    if (!success) {
      this.#setButtonState('default');
      return;
    }

    this.#setButtonState('added');
    this.#flyToCart();

    clearTimeout(this.#resetTimeout);
    this.#resetTimeout = window.setTimeout(() => this.#setButtonState('default'), ADDED_STATE_DURATION);
  }

  /**
   * @param {'default' | 'loading' | 'added'} state
   */
  #setButtonState(state) {
    const { addToCartButton } = this.refs;
    addToCartButton.dataset.atcState = state;

    if (state === 'loading') addToCartButton.setAttribute('aria-busy', 'true');
    else addToCartButton.removeAttribute('aria-busy');
  }

  async #flyToCart() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const { productImage } = this.refs;
    if (!cartIcon || !productImage || !this.#isStuck) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
    await onAnimationEnd(flyToCartElement);
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#isStuck) return;
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    if (!this.#isStuck) return;
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {ProductForm | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!productId || !section) return null;

    return /** @type {ProductForm | null} */ (
      section.querySelector(`product-form-component[data-product-id="${productId}"]`)
    );
  }

  /**
   * Gets the main variant picker of this product
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!productId || !section) return null;

    return section.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
